import { apiClient } from '@/lib/api/client'
import type { ConceptGraph } from '@/features/knowledge/api/knowledgeApi'

// ─── Types ──────────────────────────────────────────────
//
// A session is one line of enquiry against picasso-concierge from the admin; a turn is one call
// (search, chat or an MCP tool) with the concierge's answer as returned, plus what AMS adds when
// reading it back: results joined to our artworks, concepts joined to the knowledge graph, and
// the turn as a graph. Mirrors lib/picasso/console.js (keys camelCased by the API).

// PostgreSQL bigint IDs arrive as strings; keep them intact, including IDs above MAX_SAFE_INTEGER.
export type PicassoId = string | number

export type TurnMode = 'search' | 'chat' | 'tool'

/** An artwork card as the concierge returns it (search/chat products, tool results, walk lanes). */
export interface ConciergeResult {
  id: number | string
  title: string
  handle: string
  url: string
  image: string
  mockupHero?: string
  artist: string
  why: string[]
  score?: number
  price?: string
  priceFormatted?: string
  /** For a concept walk: the lane (dimension) this result came from. */
  lane?: string
  position: number
  /** Whether the id is an artwork in AMS (so it links, and has concept links in our graph). */
  inAms: boolean
}

export type ConceptRole = 'matched' | 'direction' | 'facet' | 'lane'

export interface TurnConcept {
  name: string
  role: ConceptRole
  kind?: string
  similarity?: number
  artworks?: number
  worksHere?: number
  freq?: number
  decided?: string | null
  /** Our knowledge-graph concept this name resolves to (by name or a merged name), if any. */
  ucid: string | null
  kgName?: string
  category?: string | null
  kgArtworks?: number
  weight?: number | null
}

/** The concierge's graph summary for a search (present for graph backends). */
export interface Knowledge {
  matchedConcepts?: Array<{ name: string; kind: string; similarity: number; artworks: number; decided?: string }>
  relatedDirections?: Array<{ name: string; kind: string; worksHere: number }>
  seeds?: Array<{ id: number | string; title: string; artist: string }>
  seedMode?: string
  plan?: Record<string, unknown> | null
  subgraph?: Record<string, unknown>
  addons?: { applied?: Array<{ id: string }> } & Record<string, unknown>
  [bucket: string]: unknown
}

export interface ConciergeResponse {
  query?: string
  searchId?: string | null
  message?: string
  concierge?: { message: string } | null
  followUps?: string[]
  intent?: { type: string; label: string } | null
  knowledge?: Knowledge | null
  personalization?: Record<string, unknown> | null
  dimensions?: Array<{ key: string; label: string; kind: string; blurb?: string }>
  sharedConcepts?: string[]
  meta?: { total: number; page?: number }
  [key: string]: unknown
}

export interface Turn {
  id: PicassoId
  sessionId: PicassoId
  mode: TurnMode
  tool: string | null
  input: string
  request: Record<string, unknown>
  response: ConciergeResponse | null
  error: string | null
  latencyMs: number | null
  searchId: string | null
  createdAt: string
  results: ConciergeResult[]
  concepts: TurnConcept[]
  graph: ConceptGraph
}

export interface PicassoSession {
  id: PicassoId
  title: string
  turns: number
  createdBy: PicassoId | null
  createdAt: string
  updatedAt: string
}

export interface SessionSummary extends PicassoSession {
  createdByEmail: string | null
  preview: string[]
  modes: TurnMode[]
  errors: number
}

export interface TurnInput {
  mode: TurnMode
  input: string
  options?: { limit?: number; concierge?: boolean; fast?: boolean; backend?: string; artist?: string; page?: number }
  tool?: string
  args?: Record<string, unknown>
}

/** A served shopper search from the concierge's telemetry (concierge_search_log). */
export interface ShopperSearch {
  searchId: string
  q: string
  at: number
  path: string | null
  backend: string | null
  page: number
  resultIds: Array<number | string>
  results: number
  plan: Record<string, unknown> | null
  seedMode: string | null
  addons: string[]
  intent: { type: string; label: string } | null
  session: string | null
  latencyMs: number | null
  client: string | null
  refererPath: string | null
  events: { click: number; addToCart: number; purchase: number }
}

export interface ShopperSession {
  session: string | null
  searches: ShopperSearch[]
  firstAt: number
  lastAt: number
  clicks: number
  carts: number
  zeroResults: number
}

export interface ShopperLog {
  sessions: ShopperSession[]
  totals: { searches: number; sessions: number; zeroResults: number; withClick: number; medianLatencyMs: number | null }
  clients: Array<{ name: string; n: number }>
}

export interface ShopperSearchDetail extends Omit<ShopperSearch, 'events'> {
  events: Array<{ type: string; artworkId: number | string | null; position: number | null; at: number }>
  artworks: Array<{ position: number; id: number; name?: string; status?: string; artistName?: string | null; missing?: boolean }>
}

const BASE = '/api/picasso'
// A graph search with a written message can take tens of seconds.
const SLOW = { timeout: 120000 }

export const picassoApi = {
  getTools: async (): Promise<{ data: string[]; backends: string[] }> => (await apiClient.get(`${BASE}/tools`)).data,
  getSessions: async (params: { mine?: boolean; limit?: number; offset?: number }): Promise<{ data: SessionSummary[]; total: number }> =>
    (await apiClient.get(`${BASE}/sessions`, { params })).data,
  createSession: async (title?: string): Promise<PicassoSession> => (await apiClient.post(`${BASE}/sessions`, { title })).data,
  getSession: async (id: PicassoId): Promise<Omit<PicassoSession, 'turns'> & { turns: Turn[] }> => (await apiClient.get(`${BASE}/sessions/${id}`)).data,
  renameSession: async (id: PicassoId, title: string): Promise<{ id: PicassoId; title: string }> =>
    (await apiClient.patch(`${BASE}/sessions/${id}`, { title })).data,
  deleteSession: async (id: PicassoId): Promise<void> => {
    await apiClient.delete(`${BASE}/sessions/${id}`)
  },
  runTurn: async (sessionId: PicassoId, body: TurnInput): Promise<Turn> =>
    (await apiClient.post(`${BASE}/sessions/${sessionId}/turns`, body, SLOW)).data,
  getShopperSearches: async (params: { limit?: number; client?: string; session?: string }): Promise<ShopperLog> =>
    (await apiClient.get(`${BASE}/shopper-searches`, { params })).data,
  getShopperSearch: async (searchId: string): Promise<ShopperSearchDetail> =>
    (await apiClient.get(`${BASE}/shopper-searches/${searchId}`)).data,
}
